import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Table, Pagination } from "antd";
import { GitMerge, Package, ShoppingCart, FileText, AlertTriangle, Truck, User } from "react-feather";
import InfoTile from "../../../components/common/InfoTile";
import DateRangePicker, { useDatePresetRange } from "../../../components/common/DateRangePicker";
import CustomerPicker from "../../sales/components/CustomerPicker";
import ListExportToolbar from "../../stock/components/list/ListExportToolbar";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import { useGetVehiclesQuery } from "../../masters/vehicles/vehiclesApi";
import { paths } from "../../../routes/paths";
import { useGetTransactionReconciliationQuery } from "./reconciliationApi";

// Transaction Reconciliation (Reports -> Transaction Reconciliation).
//
// THE ANCHOR IS A CLASSIFIED SUBJECT, the same "pick a master, get its 360"
// idea One Stop uses. Anchoring on the quotation (the first cut of this report)
// made it near-empty against real data: 0 of 99 LPOs and 0 of 36 stock-outs
// carry a link back to a quotation, so only 17 of 43 active customers appeared
// at all, and Vijay Optica in Systems - 9 fully-received LPOs and 10 issues, the
// richest chain in the system - rendered completely blank because Systems has no
// quotations.
//
//   CUSTOMER  benchmark = the quotation           -> LPO -> Stock-In -> Stock-Out
//   VEHICLE   benchmark = the maintenance request -> LPO -> Stock-In
//             spare items (estimated cost)
//
// Vehicles genuinely have NO issue leg - App_StockOut's only vehicle-ish column
// is a free-text purpose, so there is nothing to join on. Those cells say "n/a",
// never zero, or every vehicle would read as never delivered.
//
// Subjects are keyed (subjectBranchId, subjectId): cust_id is scoped per branch,
// so VIJAY OPTICA is 722 in Mombasa and 1583 in Systems and must never merge.
//
// THE BENCHMARK IS NOT A MOVEMENT. It is what was promised or estimated; the
// other legs are what actually happened. An item with nothing against it has not
// been under-delivered - nothing has been actioned for it yet, so such rows read
// "No movement yet" and stay out of the short/over counts. Where a subject has
// no benchmark at all, "margin" is not a margin and is suppressed rather than
// shown as a huge negative.
//
// MONEY: VAT-inclusive throughout for a customer (the quoted figure is grossed
// up server-side from the stored ex-VAT LineAmount by the line's own VAT %,
// reproducing the quotation screen). A vehicle's estimate is hand-entered with
// no VAT field anywhere, so it is shown as written and labelled "Estimated".

const money = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const CUSTOMERS_PER_PAGE = 8;
const ITEMS_PER_GROUP_PAGE = 15;

const SUBJECT = {
  CUSTOMER: { label: "Customer", icon: User, benchmark: "Quoted", benchmarkLong: "Quoted (incl VAT)", hasIssueLeg: true },
  VEHICLE: { label: "Vehicle", icon: Truck, benchmark: "Estimated", benchmarkLong: "Estimated spares", hasIssueLeg: false },
};

const ROUTE = {
  PURCHASED: { label: "Purchased", cls: "badge-linesuccess", hint: "Bought for this job - cost is what was received" },
  FROM_STOCK: { label: "From stock", cls: "badge-lineinfo", hint: "Issued from existing stock - no LPO is expected" },
  // Neutral by design: the benchmark is a promise, so "nothing against it yet"
  // is a state, not a failure. badge-linesecondary, not badge-linelight - the
  // latter is used in a few screens but has no CSS rule anywhere.
  NOT_SUPPLIED: { label: "No movement yet", cls: "badge-linesecondary", hint: "On the benchmark, but no LPO or issue recorded against it yet" },
};

const RouteBadge = ({ route }) => {
  const r = ROUTE[route] || { label: route, cls: "badge-linedanger", hint: "" };
  return <span className={`badges ${r.cls}`} title={r.hint}>{r.label}</span>;
};

/** Signed figure, coloured by whether it is good or bad for THIS column. */
const Delta = ({ value, goodWhenPositive = true, format = money }) => {
  const n = Number(value || 0);
  if (n === 0) return <span className="text-muted">-</span>;
  const good = goodWhenPositive ? n > 0 : n < 0;
  return (
    <span className={good ? "text-success fw-semibold" : "text-danger fw-semibold"}>
      {n > 0 ? "+" : ""}{format(n)}
    </span>
  );
};

/** Qty over its money value, so each leg reads as one figure with its amount. */
const QtyAmount = ({ q, amount, na = false, naHint }) => {
  if (na) return <span className="text-muted" title={naHint}>n/a</span>;
  return (
    <>
      <div>{qty(q)}</div>
      <div className="text-muted" style={{ fontSize: 11.5 }}>{money(amount)}</div>
    </>
  );
};

/**
 * Chips for the documents behind a row. `to` may be null - a vehicle maintenance
 * request has no detail route of its own (it lives inside the Vehicles screen),
 * so it renders as a plain label instead of a dead link.
 */
const DocLinks = ({ docs, to, empty }) => {
  if (!docs || docs.length === 0) return empty ? <span className="text-muted">{empty}</span> : null;
  return (
    <div className="d-flex flex-wrap gap-1">
      {docs.map((d) =>
        to ? (
          <Link key={d.recordGuid} to={to(d.recordGuid)} className="badges badge-linesecondary text-decoration-none" title="Open this document">
            {d.number || "(no number)"}
          </Link>
        ) : (
          <span key={d.recordGuid} className="badges badge-linesecondary" title="This document has no detail screen of its own">
            {d.number || "(no number)"}
          </span>
        )
      )}
    </div>
  );
};

/** Unique documents across a set of rows, for the per-subject header. */
const collectDocs = (items, field) => {
  const map = new Map();
  items.forEach((r) => (r[field] || []).forEach((d) => map.set(d.recordGuid, d)));
  return [...map.values()];
};

const hasMovement = (r) => Number(r.lpoQty) > 0 || Number(r.inQty) > 0 || Number(r.outQty) > 0;
const sumBy = (items, f) => items.reduce((s, r) => s + Number(f(r) || 0), 0);

const EXPORT_COLUMNS = [
  { header: "Type", value: (r) => SUBJECT[r.subjectType]?.label || r.subjectType },
  { header: "Subject", value: (r) => r.subjectName || "" },
  { header: "Branch", value: (r) => r.branchCode || "" },
  { header: "Item", value: (r) => r.itemName || "" },
  { header: "Route", value: (r) => ROUTE[r.supplyRoute]?.label || r.supplyRoute },
  { header: "Benchmark Qty", align: "right", value: (r) => r.quotedQty },
  { header: "Benchmark Amount", align: "right", value: (r) => r.quotedAmount },
  { header: "LPO Qty", align: "right", value: (r) => r.lpoQty },
  { header: "LPO Amount", align: "right", value: (r) => r.lpoAmount },
  { header: "Received Qty", align: "right", value: (r) => r.inQty },
  { header: "Received Amount", align: "right", value: (r) => r.inAmount },
  { header: "Issued Qty", align: "right", value: (r) => (r.subjectType === "VEHICLE" ? "" : r.outQty) },
  { header: "Issued Amount", align: "right", value: (r) => (r.subjectType === "VEHICLE" ? "" : r.outAmount) },
  { header: "Qty Variance", align: "right", value: (r) => (hasMovement(r) ? r.qtyVariance : "") },
  { header: "Actual Cost", align: "right", value: (r) => r.actualCost },
  { header: "Margin", align: "right", value: (r) => (Number(r.quotedAmount) > 0 ? r.valueVariance : "") },
  { header: "Benchmark Docs", value: (r) => (r.benchmarkDocs || []).map((d) => d.number).join(", ") },
  { header: "LPOs", value: (r) => (r.lpoDocs || []).map((d) => d.number).join(", ") },
  { header: "Stock-Ins", value: (r) => (r.stockInDocs || []).map((d) => d.number).join(", ") },
  { header: "Stock-Outs", value: (r) => (r.stockOutDocs || []).map((d) => d.number).join(", ") },
];

// The PDF is the printed sheet, so it drops the document-reference columns -
// long, unclickable on paper, and already summarised per subject.
const PDF_COLUMNS = EXPORT_COLUMNS.filter(
  (c) => !["Benchmark Docs", "LPOs", "Stock-Ins", "Stock-Outs", "Branch"].includes(c.header)
);

const ReconciliationReportPage = () => {
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("THIS_MONTH");
  const [subjectType, setSubjectType] = useState("ALL");
  const [customer, setCustomer] = useState(null);
  const [vehicleId, setVehicleId] = useState("");
  const [routeFilter, setRouteFilter] = useState("ALL");
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [page, setPage] = useState(1);

  const custId = customer?.customer?.custId;

  // Only fetched when it can actually be used - the vehicle picker is hidden
  // for a customer-only report.
  const { data: vehicles = [] } = useGetVehiclesQuery(
    { branchId: selectedBranchId ?? undefined },
    { skip: subjectType === "CUSTOMER" }
  );

  const { data: rows = [], isFetching, isError, error } = useGetTransactionReconciliationQuery({
    branchId: selectedBranchId ?? undefined,
    custId: custId ?? undefined,
    vehicleId: vehicleId || undefined,
    subjectType,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;
  const stamp = `${dateFrom || "start"}_${dateTo || "today"}`;

  const filtered = useMemo(
    () =>
      rows.filter((r) => {
        if (routeFilter !== "ALL" && r.supplyRoute !== routeFilter) return false;
        // "Needs attention" means a real discrepancy against the benchmark:
        // something moved and came out different, or something moved that the
        // benchmark never mentioned. An item awaiting action is not one.
        if (onlyProblems && !((hasMovement(r) && Number(r.qtyVariance) !== 0) || r.isUnquoted)) return false;
        return true;
      }),
    [rows, routeFilter, onlyProblems]
  );

  const totals = useMemo(
    () =>
      filtered.reduce(
        (a, r) => ({
          quotedQty: a.quotedQty + Number(r.quotedQty || 0),
          quotedAmount: a.quotedAmount + Number(r.quotedAmount || 0),
          lpoQty: a.lpoQty + Number(r.lpoQty || 0),
          lpoAmount: a.lpoAmount + Number(r.lpoAmount || 0),
          inQty: a.inQty + Number(r.inQty || 0),
          inAmount: a.inAmount + Number(r.inAmount || 0),
          outQty: a.outQty + Number(r.outQty || 0),
          outAmount: a.outAmount + Number(r.outAmount || 0),
          // Variance only counts where something actually moved - an untouched
          // benchmark line would otherwise register as a full shortfall.
          qtyVariance: a.qtyVariance + (hasMovement(r) ? Number(r.qtyVariance || 0) : 0),
          actualCost: a.actualCost + Number(r.actualCost || 0),
          // Margin is only meaningful where there IS a benchmark.
          valueVariance: a.valueVariance + (Number(r.quotedAmount) > 0 ? Number(r.valueVariance || 0) : 0),
          costWithoutQuote: a.costWithoutQuote + (Number(r.quotedAmount) > 0 ? 0 : Number(r.actualCost || 0)),
          short: a.short + (hasMovement(r) && Number(r.qtyVariance) < 0 ? 1 : 0),
          over: a.over + (hasMovement(r) && Number(r.qtyVariance) > 0 ? 1 : 0),
          unquoted: a.unquoted + (r.isUnquoted ? 1 : 0),
          pending: a.pending + (!hasMovement(r) && !r.isUnquoted ? 1 : 0),
        }),
        {
          quotedQty: 0, quotedAmount: 0, lpoQty: 0, lpoAmount: 0, inQty: 0, inAmount: 0,
          outQty: 0, outAmount: 0, qtyVariance: 0, actualCost: 0, valueVariance: 0,
          costWithoutQuote: 0, short: 0, over: 0, unquoted: 0, pending: 0,
        }
      ),
    [filtered]
  );

  // Grouped by SUBJECT - the heading the reader is actually looking for.
  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = `${r.subjectType}:${r.subjectBranchId}:${r.subjectId}`;
      if (!map.has(key)) map.set(key, { key, head: r, items: [] });
      map.get(key).items.push(r);
    });
    return [...map.values()].sort(
      (a, b) =>
        a.head.subjectType.localeCompare(b.head.subjectType) ||
        (a.head.subjectName || "").localeCompare(b.head.subjectName || "")
    );
  }, [filtered]);

  // Any filter change can shorten the list under the current page.
  useEffect(() => {
    setPage(1);
  }, [routeFilter, onlyProblems, custId, vehicleId, subjectType, selectedBranchId, dateFrom, dateTo]);

  // Picking one subject type makes the other's filter meaningless - clear it so
  // a stale customer can't silently keep narrowing a vehicle report.
  useEffect(() => {
    if (subjectType === "VEHICLE") setCustomer(null);
    if (subjectType === "CUSTOMER") setVehicleId("");
  }, [subjectType]);

  const pageCount = Math.max(1, Math.ceil(groups.length / CUSTOMERS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pageGroups = groups.slice((safePage - 1) * CUSTOMERS_PER_PAGE, safePage * CUSTOMERS_PER_PAGE);

  const exportTotals = useMemo(
    () => ({
      Subject: `${groups.length} subjects`,
      "Benchmark Qty": totals.quotedQty,
      "Benchmark Amount": totals.quotedAmount,
      "LPO Qty": totals.lpoQty,
      "LPO Amount": totals.lpoAmount,
      "Received Qty": totals.inQty,
      "Received Amount": totals.inAmount,
      "Issued Qty": totals.outQty,
      "Issued Amount": totals.outAmount,
      "Actual Cost": totals.actualCost,
      Margin: totals.valueVariance,
    }),
    [groups.length, totals]
  );

  // Columns depend on the subject: a vehicle has no issue leg and its benchmark
  // is an estimate, not a VAT-inclusive quote. Built per group so an ALL report
  // labels each table correctly instead of picking one and being wrong for half.
  const columnsFor = (type) => {
    const meta = SUBJECT[type] || SUBJECT.CUSTOMER;
    return [
      {
        title: "Item",
        dataIndex: "itemName",
        width: 230,
        fixed: "left",
        render: (v, r) => (
          <>
            <div>{v}</div>
            <div className="text-muted" style={{ fontSize: 11.5 }}>
              {r.productCode != null ? `#${r.productCode}` : "free text"}
              {r.isUnquoted ? " · not on the benchmark" : ""}
            </div>
          </>
        ),
      },
      { title: "Route", dataIndex: "supplyRoute", width: 130, render: (v) => <RouteBadge route={v} /> },
      {
        title: meta.benchmark,
        align: "right",
        width: 110,
        render: (_, r) => <QtyAmount q={r.quotedQty} amount={r.quotedAmount} />,
      },
      {
        title: "LPO",
        align: "right",
        width: 110,
        render: (_, r) => (
          <QtyAmount q={r.lpoQty} amount={r.lpoAmount} na={r.supplyRoute === "FROM_STOCK"} naHint="Issued from stock - no LPO expected" />
        ),
      },
      {
        title: "Received",
        align: "right",
        width: 110,
        render: (_, r) => (
          <QtyAmount q={r.inQty} amount={r.inAmount} na={r.supplyRoute === "FROM_STOCK"} naHint="Issued from stock - nothing was received against an LPO" />
        ),
      },
      {
        title: "Issued",
        align: "right",
        width: 110,
        render: (_, r) => (
          <QtyAmount
            q={r.outQty}
            amount={r.outAmount}
            na={!meta.hasIssueLeg}
            naHint="Spares are consumed on the vehicle - there is no customer issue to record"
          />
        ),
      },
      {
        title: "Qty var",
        dataIndex: "qtyVariance",
        align: "right",
        width: 95,
        render: (v, r) =>
          hasMovement(r) ? (
            <Delta value={v} goodWhenPositive={false} format={qty} />
          ) : (
            <span className="text-muted" title="Nothing has moved against this line yet, so there is nothing to compare">-</span>
          ),
      },
      { title: "Actual cost", dataIndex: "actualCost", align: "right", width: 115, render: (v) => money(v) },
      {
        title: "Margin",
        dataIndex: "valueVariance",
        align: "right",
        width: 115,
        // Without a benchmark there is nothing to measure against, so "benchmark
        // less cost" is just the cost negated - a number that reads as a loss.
        render: (v, r) =>
          Number(r.quotedAmount) > 0 ? (
            <Delta value={v} />
          ) : (
            <span className="text-muted" title="Nothing was quoted or estimated for this item, so there is no margin to measure">n/a</span>
          ),
      },
      {
        title: "Documents",
        width: 215,
        render: (_, r) => (
          <div className="d-flex flex-column gap-1" style={{ fontSize: 11.5 }}>
            <DocLinks docs={r.benchmarkDocs} to={type === "CUSTOMER" ? paths.quotationDetail : null} empty="" />
            <DocLinks docs={r.lpoDocs} to={paths.stockLpoDetail} empty="" />
            <DocLinks docs={r.stockInDocs} to={paths.stockInDetail} empty="" />
            <DocLinks docs={r.stockOutDocs} to={paths.stockOutDetail} empty="" />
          </div>
        ),
      },
    ];
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Transaction Reconciliation</h4>
              <h6>What was promised, measured against what was actually bought, received and issued</h6>
            </div>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-6 col-lg-2">
                <label className="form-label text-muted" style={{ fontSize: 12 }}>Subject</label>
                <select className="form-control" value={subjectType} onChange={(e) => setSubjectType(e.target.value)}>
                  <option value="ALL">Customers &amp; vehicles</option>
                  <option value="CUSTOMER">Customers</option>
                  <option value="VEHICLE">Vehicles</option>
                </select>
              </div>

              {subjectType !== "VEHICLE" && (
                <div className="col-12 col-lg-3">
                  <label className="form-label text-muted" style={{ fontSize: 12 }}>Customer</label>
                  <CustomerPicker branchId={selectedBranchId} isClearable value={customer} onChange={setCustomer} />
                </div>
              )}

              {subjectType !== "CUSTOMER" && (
                <div className="col-12 col-lg-3">
                  <label className="form-label text-muted" style={{ fontSize: 12 }}>Vehicle</label>
                  <select className="form-control" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                    <option value="">All vehicles</option>
                    {vehicles.map((v) => (
                      <option key={v.vehicleId} value={v.vehicleId}>
                        {v.regNo}
                        {v.brand ? ` - ${v.brand}${v.model ? ` ${v.model}` : ""}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="col-12 col-lg-3">
                <label className="form-label text-muted" style={{ fontSize: 12 }}>Activity date</label>
                <DateRangePicker preset={preset} setPreset={setPreset} dateFrom={dateFrom} setDateFrom={setDateFrom} dateTo={dateTo} setDateTo={setDateTo} />
              </div>
              <div className="col-6 col-lg-2">
                <label className="form-label text-muted" style={{ fontSize: 12 }}>Supply route</label>
                <select className="form-control" value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)}>
                  <option value="ALL">All routes</option>
                  <option value="PURCHASED">Purchased for the job</option>
                  <option value="FROM_STOCK">From existing stock</option>
                  <option value="NOT_SUPPLIED">No movement yet</option>
                </select>
              </div>
              <div className="col-6 col-lg-2">
                <div className="form-check mt-4">
                  <input className="form-check-input" type="checkbox" id="onlyProblems" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
                  <label className="form-check-label" htmlFor="onlyProblems" style={{ fontSize: 13 }}>Only mismatches</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {isError && <div className="alert alert-danger">{error?.data?.message || error?.message || "Could not load the report."}</div>}

        {/* ---- Summary ---- */}
        <div className="row g-2 mb-3">
          <div className="col-6 col-xl-3">
            <InfoTile label={subjectType === "VEHICLE" ? "Estimated spares" : "Benchmark (incl VAT)"}>
              <FileText size={13} className="me-1" />
              {money(totals.quotedAmount)}
              <span className="fw-normal text-muted"> · {qty(totals.quotedQty)} units</span>
            </InfoTile>
          </div>
          <div className="col-6 col-xl-3">
            <InfoTile label="Actual cost">
              <ShoppingCart size={13} className="me-1" />
              {money(totals.actualCost)}
              <span className="fw-normal text-muted"> · received {money(totals.inAmount)}</span>
            </InfoTile>
          </div>
          <div className="col-6 col-xl-3">
            <InfoTile label="Issued to customers">
              <Package size={13} className="me-1" />
              {money(totals.outAmount)}
              <span className="fw-normal text-muted"> · {qty(totals.outQty)} units</span>
            </InfoTile>
          </div>
          <div className="col-6 col-xl-3">
            {/* Margin, not an error - negative means it cost more than promised.
                Only benchmarked items contribute. */}
            <InfoTile label="Margin (where benchmarked)" tone={totals.valueVariance < 0 ? "maroon" : "success"}>
              <GitMerge size={13} className="me-1" />
              {money(totals.valueVariance)}
              {totals.costWithoutQuote > 0 && (
                <span className="fw-normal text-muted"> · {money(totals.costWithoutQuote)} cost unbenchmarked</span>
              )}
            </InfoTile>
          </div>
        </div>

        {(totals.short > 0 || totals.over > 0 || totals.unquoted > 0 || totals.pending > 0) && (
          <div className="alert alert-light border d-flex align-items-center gap-2">
            <AlertTriangle size={15} className="text-warning flex-shrink-0" />
            <span style={{ fontSize: 13 }}>
              {totals.short > 0 && <><strong>{totals.short}</strong> item{totals.short === 1 ? "" : "s"} came in under the benchmark quantity. </>}
              {totals.over > 0 && <><strong>{totals.over}</strong> came in above it. </>}
              {totals.unquoted > 0 && <><strong>{totals.unquoted}</strong> moved without being on any benchmark. </>}
              {totals.pending > 0 && <><strong>{totals.pending}</strong> benchmark line{totals.pending === 1 ? "" : "s"} with no movement against {totals.pending === 1 ? "it" : "them"} yet. </>}
              {(totals.short > 0 || totals.over > 0) && <>Net quantity variance {qty(totals.qtyVariance)} across the items that moved.</>}
            </span>
          </div>
        )}

        {/* ---- Detail, one card per subject ---- */}
        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
              <h5 className="mb-0">
                Detail &mdash; {groups.length} subject{groups.length === 1 ? "" : "s"}, {filtered.length} item{filtered.length === 1 ? "" : "s"}
                {pageCount > 1 && <span className="text-muted fw-normal"> · page {safePage} of {pageCount}</span>}
              </h5>
              {/* Export always covers EVERY filtered row, not just the page on
                  screen - a printed sheet that silently stopped at 8 subjects
                  would be worse than no export at all. */}
              <ListExportToolbar
                title="Transaction Reconciliation"
                subtitleLines={[
                  `Activity ${dateFrom || "start"} to ${dateTo || "today"}`,
                  `${groups.length} subjects, ${filtered.length} items - customer amounts include VAT`,
                ]}
                branchCode={branchCode}
                columns={EXPORT_COLUMNS}
                pdfColumns={PDF_COLUMNS}
                rows={filtered}
                totals={exportTotals}
                groupBy={(r) => `${SUBJECT[r.subjectType]?.label || r.subjectType}: ${r.subjectName || "(none)"}`}
                groupTotals
                fileName={`TransactionReconciliation_${stamp}`}
                orientation="landscape"
              />
            </div>

            {isFetching && <div className="text-muted small mb-2">Loading...</div>}
            {!isFetching && groups.length === 0 && (
              <div className="text-muted">
                Nothing to reconcile for this filter. A customer appears once there is a quotation, a customer-specific
                LPO or an issue against them; a vehicle appears once it has a maintenance request or an LPO raised from
                one, in the selected period.
              </div>
            )}

            {pageGroups.map((g) => {
              const type = g.head.subjectType;
              const meta = SUBJECT[type] || SUBJECT.CUSTOMER;
              const Icon = meta.icon;
              const benchmarkDocs = collectDocs(g.items, "benchmarkDocs");
              const lpoDocs = collectDocs(g.items, "lpoDocs");
              const inDocs = collectDocs(g.items, "stockInDocs");
              const outDocs = collectDocs(g.items, "stockOutDocs");
              const gBench = sumBy(g.items, (r) => r.quotedAmount);
              const gCost = sumBy(g.items, (r) => r.actualCost);
              const gIssued = sumBy(g.items, (r) => r.outAmount);
              return (
                <div key={g.key} className="mb-4">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
                    <div className="d-flex align-items-center gap-2">
                      <Icon size={15} className="text-muted flex-shrink-0" />
                      <div>
                        {/* The subject is the heading - that is who the reader
                            is looking for. Documents are the reference below. */}
                        {type === "VEHICLE" ? (
                          <Link to={paths.vehicleEdit(g.head.subjectId)} className="fw-semibold text-decoration-none" style={{ fontSize: 15 }}>
                            {g.head.subjectName || "(no vehicle)"}
                          </Link>
                        ) : (
                          <span className="fw-semibold" style={{ fontSize: 15 }}>{g.head.subjectName || "(no customer)"}</span>
                        )}
                        {g.head.subjectDetail && <span className="text-muted"> · {g.head.subjectDetail}</span>}
                        {g.head.branchCode && <span className="text-muted"> · {g.head.branchCode}</span>}
                        <span className="text-muted"> · {g.items.length} item{g.items.length === 1 ? "" : "s"}</span>
                      </div>
                    </div>
                    <div className="small text-muted">
                      {gBench > 0 ? <>{meta.benchmark.toLowerCase()} {money(gBench)} · </> : <>no benchmark · </>}
                      cost {money(gCost)}
                      {meta.hasIssueLeg && <> · issued {money(gIssued)}</>}
                    </div>
                  </div>

                  {/* Every document behind this subject, one click away. */}
                  {(benchmarkDocs.length > 0 || lpoDocs.length > 0 || inDocs.length > 0 || outDocs.length > 0) && (
                    <div className="d-flex flex-wrap align-items-center gap-3 mb-2" style={{ fontSize: 12 }}>
                      {benchmarkDocs.length > 0 && (
                        <span className="d-flex align-items-center gap-1">
                          <span className="text-muted">{type === "VEHICLE" ? "Request" : "Quotation"}</span>
                          <DocLinks docs={benchmarkDocs} to={type === "CUSTOMER" ? paths.quotationDetail : null} empty="" />
                        </span>
                      )}
                      {lpoDocs.length > 0 && (
                        <span className="d-flex align-items-center gap-1">
                          <span className="text-muted">LPO</span>
                          <DocLinks docs={lpoDocs} to={paths.stockLpoDetail} empty="" />
                        </span>
                      )}
                      {inDocs.length > 0 && (
                        <span className="d-flex align-items-center gap-1">
                          <span className="text-muted">Received</span>
                          <DocLinks docs={inDocs} to={paths.stockInDetail} empty="" />
                        </span>
                      )}
                      {outDocs.length > 0 && (
                        <span className="d-flex align-items-center gap-1">
                          <span className="text-muted">Issued</span>
                          <DocLinks docs={outDocs} to={paths.stockOutDetail} empty="" />
                        </span>
                      )}
                    </div>
                  )}

                  <Table
                    rowKey={(r) => `${g.key}-${r.productCode ?? r.itemName}`}
                    size="small"
                    columns={columnsFor(type)}
                    dataSource={g.items}
                    pagination={
                      g.items.length > ITEMS_PER_GROUP_PAGE
                        ? { pageSize: ITEMS_PER_GROUP_PAGE, size: "small", showSizeChanger: false, showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} items` }
                        : false
                    }
                    scroll={{ x: 1350 }}
                  />
                </div>
              );
            })}

            {pageCount > 1 && (
              <div className="d-flex justify-content-end mt-2">
                <Pagination
                  current={safePage}
                  total={groups.length}
                  pageSize={CUSTOMERS_PER_PAGE}
                  onChange={setPage}
                  showSizeChanger={false}
                  showTotal={(t, r) => `Subjects ${r[0]}-${r[1]} of ${t}`}
                />
              </div>
            )}
          </div>
        </div>

        <small className="text-muted d-block mb-4">
          Grouped by <strong>subject</strong> &mdash; a customer or a vehicle &mdash; because most real movements are
          not linked back to a quotation. The <strong>benchmark</strong> is what was promised: the quotation for a
          customer (VAT-inclusive), the maintenance request&rsquo;s estimated spares for a vehicle (hand-entered, no VAT
          field). LPO, Received and Issued are the actual movements measured against it. A vehicle has
          <strong> no issue leg</strong> &mdash; spares are consumed on the vehicle &mdash; so that column reads
          &ldquo;n/a&rdquo;. <strong>Route</strong> decides which other columns apply: a <em>From stock</em> item
          legitimately has no LPO or Stock-In, and a line with nothing against it yet shows &ldquo;No movement yet&rdquo;
          rather than counting as short. <strong>Margin</strong> is benchmark less actual cost, shown only where a
          benchmark exists. Export covers every filtered row, not just the page on screen.
        </small>
      </div>
    </div>
  );
};

export default ReconciliationReportPage;
